import { Dropdown, DropdownItem, Input } from '@distribusion/owl-frontend-ui'
import { endOfDay, startOfDay, isAfter, addMinutes, format } from 'date-fns'
import React, { useMemo } from 'react'

import { useSettings } from '@queries/settings'

import '@ui/TimeSelector/index.scss'

export const generateTimeOptions = (loadConnections: boolean, timeAllowance: number): string[] => {
  const start = startOfDay(new Date())
  const end = endOfDay(new Date())

  let step: number = 60

  if (loadConnections) {
    /* istanbul ignore next: can't test it locally in e2e */ if (timeAllowance === 90) {
      step = 30
    }
  }

  const options: string[] = []
  let current = start

  while (!isAfter(current, end)) {
    options.push(format(current, 'HH:mm'))
    current = addMinutes(current, step)
  }

  return options
}

interface TimeSelectorProps {
  onChange: (item: string) => void
  label: string
  options?: DropdownItem<string>[]
  value?: string
  errorMessage?: string
  loadConnections: boolean
  timeAllowance: number
}

const TimeSelectorDropdown: React.FC<TimeSelectorProps> = ({
  onChange,
  label,
  options,
  value,
  errorMessage,
  loadConnections,
  timeAllowance,
}) => {
  const timeOptions = generateTimeOptions(loadConnections, timeAllowance)

  const dropdownItems = useMemo(() => {
    const baseItems = options || timeOptions.map(option => ({ value: option, selectedLabel: option }))

    if (value && !baseItems.some(item => item.value === value)) {
      const customItem = { value, selectedLabel: value }
      const insertIndex = baseItems.findIndex(item => item.value > value)
      if (insertIndex === -1) {
        /* istanbul ignore next: this is not e2e test */ return [...baseItems, customItem]
      } else {
        return [...baseItems.slice(0, insertIndex), customItem, ...baseItems.slice(insertIndex)]
      }
    }

    return baseItems
  }, [options, timeOptions, value])

  return (
    <Dropdown
      className="time-selector"
      value={value}
      onChange={onChange}
      checkedMark
      label={label}
      items={dropdownItems}
      errorMessage={errorMessage}
    />
  )
}

const TimeSelector: React.FC<TimeSelectorProps> = props => {
  const [
    {
      userInterface: {
        time: { textInputEnabled },
      },
    },
  ] = useSettings()

  if (textInputEnabled && !props.options) {
    const { onChange, label, value, errorMessage } = props

    return (
      <Input
        className="time-selector"
        type="time"
        value={value}
        label={label}
        errorMessage={errorMessage}
        onChange={onChange}
        required
        ariaLabel={label}
      />
    )
  }

  return <TimeSelectorDropdown {...props} />
}

export default TimeSelector
